import React, { Fragment, useEffect, useContext, ReactNode, useState } from 'react';
import { AuthContext } from "../contexts/auth";
import { Dialog, Menu, Transition } from '@headlessui/react'
import { Bars3CenterLeftIcon, XMarkIcon, UserCircleIcon, PlusCircleIcon, WrenchScrewdriverIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { menuItems } from '../stores/menuData';
import { Link, useLocation } from 'wouter';
import { useAtom } from 'jotai';
import { currentPageStore, firmePendingStore } from '../stores/mainStore';
import { employeeSignedDocumentsStats } from '../services/api_dipendenti';
import CustomTooltipComponent from '../components/custom_tooltip_component';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { canViewOwnSignedDocs, countAwaitingEmployeeSignature, getSignedDocs } from '../services/api_firme';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

/**
 * Dropdown config: aggancia i figli ad una voce "parent" identificata per href.
 * Puoi aggiungere qui altri dropdown in futuro.
 */
const DROPDOWN_BY_HREF = {
  '/shift_management': {
    children: [
      { name: 'Crea nuovo turno', href: '/shift_management/create', Icon: PlusCircleIcon },
      { name: 'Visualizza turni', href: '/shift_management/my_shifts', Icon: ManageSearchIcon },
    ],
  },
  '/working_area': {
    children: [
      { name: 'Presenze Area', href: '/working_area/presences', Icon: UserGroupIcon },
      { name: 'Gestisci Aree', href: '/working_area/manage', Icon: WrenchScrewdriverIcon },
    ],
  },
};

const isPathActive = (currentPage, href) => {
  if (!currentPage || !href) return false;
  if (currentPage === href) return true;
  // se vuoi considerare attivi anche sotto-path:
  // return currentPage.startsWith(href);
  return false;
};

const DropdownNavItem = ({
  item,
  currentPage,
  setCurrentPage,
  desktopSidebarOpen,
}) => {
  const config = DROPDOWN_BY_HREF[item.href];
  const children = config?.children || [];

  const anyChildActive = children.some((c) => isPathActive(currentPage, c.href));
  const parentActive = isPathActive(currentPage, item.href) || anyChildActive;

  const btnRef = React.useRef(null);
  const flyoutRef = React.useRef(null);

  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const [open, setOpen] = useState(anyChildActive);

  useEffect(() => {
    if (anyChildActive) setOpen(true);
  }, [anyChildActive]);

  // Chiudi il flyout quando clicchi su un child
  const handleChildClick = (href) => {
    setCurrentPage(href);
    setOpen(false);
  };

  // Calcolo posizione SOLO quando sidebar collassata
  useEffect(() => {
    if (!open || desktopSidebarOpen) return;
    if (!btnRef.current) return;

    const rect = btnRef.current.getBoundingClientRect();
    setFlyoutPos({
      top: rect.top,
      left: rect.right + 8,
    });
  }, [open, desktopSidebarOpen]);

  // CHIUSURA CLICK FUORI + ESC
  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      const t = e.target;
      const inBtn = btnRef.current?.contains(t);
      const inFlyout = flyoutRef.current?.contains(t);

      if (!inBtn && !inFlyout) {
        setOpen(false);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    // capture = true aiuta con overlay/modal e componenti che stoppropagation
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={classNames(
          parentActive ? 'bg-indigo-100' : 'hover:bg-gray-50 hover:text-gray-900',
          'group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium text-indigo-800'
        )}
      >
        <CustomTooltipComponent title={desktopSidebarOpen ? '' : item.name} placement="right">
          <item.icon
            className={classNames(
              parentActive ? 'text-indigo-900' : 'text-indigo-800 group-hover:text-indigo-900',
              'mr-3 h-6 w-6 flex-shrink-0'
            )}
            aria-hidden="true"
          />
        </CustomTooltipComponent>

        {desktopSidebarOpen && (
          <span className="flex-1 text-left text-indigo-800">
            {item.name}
          </span>
        )}

        {desktopSidebarOpen && (
          <ChevronUpDownIcon
            className={classNames(
              'h-5 w-5 text-gray-400 transition-transform',
              open ? 'rotate-180' : 'rotate-0'
            )}
            aria-hidden="true"
          />
        )}
      </button>

      {/* Sidebar APERTA: lista inline sotto */}
      {desktopSidebarOpen && open && (
        <div ref={flyoutRef} className="mt-1 space-y-1">
          {children.map((c) => {
            const active = isPathActive(currentPage, c.href);
            const ChildIcon = c.Icon;

            return (
              <Link
                key={c.href}
                to={c.href}
                onClick={() => handleChildClick(c.href)}
                className={classNames(
                  active ? 'bg-indigo-100 text-indigo-900' : 'text-indigo-800 hover:bg-gray-50 hover:text-gray-900',
                  'group flex items-center rounded-md px-2 py-2 pl-11 text-sm font-medium'
                )}
                aria-current={active ? 'page' : undefined}
              >
                {ChildIcon && (
                  <ChildIcon className="mr-2 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                )}
                {c.name}
              </Link>
            );
          })}
        </div>
      )}

      {/* Sidebar COLLASSATA: flyout FIXED */}
      {!desktopSidebarOpen && open && (
        <div
          ref={flyoutRef}
          className="fixed w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 py-1"
          style={{
            top: flyoutPos.top,
            left: flyoutPos.left,
            zIndex: 99999,
          }}
        >
          <div className="px-3 py-2 text-xs font-semibold text-gray-500">
            {item.name}
          </div>

          {children.map((c) => {
            const active = isPathActive(currentPage, c.href);
            const ChildIcon = c.Icon;

            return (
              <Link
                key={c.href}
                to={c.href}
                onClick={() => handleChildClick(c.href)}
                className={classNames(
                  active ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:font-semibold',
                  'flex items-center px-3 py-2 text-sm'
                )}
                aria-current={active ? 'page' : undefined}
              >
                {ChildIcon && (
                  <span className="mr-2 flex items-center justify-center">
                    <ChildIcon className="h-5 w-5" />
                  </span>
                )}
                {c.name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SimpleNavItem = ({ item, currentPage, setCurrentPage, desktopSidebarOpen, badge }) => {
  const active = isPathActive(currentPage, item.href);

  return (
    <Link
      key={item.name}
      to={item.href}
      onClick={() => setCurrentPage(item.href)}
      className={classNames(
        active ? "bg-indigo-100" : "hover:bg-gray-50 hover:text-gray-900",
        'group flex items-center rounded-md px-2 py-2 text-sm text-indigo-800'
      )}
      aria-current={active ? "page" : undefined}
    >
      <CustomTooltipComponent title={desktopSidebarOpen ? '' : item.name} placement="right">
        <item.icon
          className={classNames(
            active ? 'text-indigo-900' : 'text-indigo-800 group-hover:text-indigo-900',
            'mr-3 h-6 w-6 flex-shrink-0'
          )}
          aria-hidden="true"
        />
      </CustomTooltipComponent>

      <span className={classNames("inline-block text-indigo-800", !desktopSidebarOpen && "lg:hidden")}>
        {item.name}
      </span>
      {badge > 0 && (
        <span className={classNames(
          "ml-auto inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700",
          !desktopSidebarOpen && "lg:hidden"
        )}>
          {badge}
        </span>
      )}
    </Link>
  );
};

const renderMenuGroup = ({
  gestioneKey,
  currentPage,
  setCurrentPage,
  desktopSidebarOpen,
  badges = {},
  canViewLetters = false,
}) => {
  return menuItems
    .filter((item) =>
      item.gestione === gestioneKey &&
      (item.href !== '/lettere' || canViewLetters)
    )
    .map((item) => {
      // Dropdown “Gestione Turni” (href /shift_management)
      if (DROPDOWN_BY_HREF[item.href]) {
        return (
          <DropdownNavItem
            key={item.href}
            item={item}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            desktopSidebarOpen={desktopSidebarOpen}
          />
        );
      }

      return (
        <SimpleNavItem
          key={item.href}
          item={item}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          desktopSidebarOpen={desktopSidebarOpen}
          badge={item.badgeKey ? badges[item.badgeKey] : 0}
        />
      );
    });
};

const SidebarSectionHeader = ({
  title,
  icon: Icon,
  desktopSidebarOpen,
  showDivider = false,
}) => {
  return (
    <div className="mb-3 mt-5">

      {/* Divisorio orizzontale opzionale */}
      {showDivider && (
        <div
          className="mb-5 border-t border-gray-300"
          aria-hidden="true"
        />
      )}

      <div
        className={classNames(
          "flex items-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 lg:bg-white",
          !desktopSidebarOpen && "lg:justify-center lg:px-2"
        )}
      >
        <CustomTooltipComponent
          title={desktopSidebarOpen ? "" : title}
          placement="right"
        >
          <Icon
            className="h-5 w-5 shrink-0 text-indigo-700"
            aria-hidden="true"
          />
        </CustomTooltipComponent>

        <span
          className={classNames(
            "ml-2 text-xs font-bold uppercase tracking-wide text-indigo-800",
            !desktopSidebarOpen && "lg:hidden"
          )}
        >
          {title}
        </span>
      </div>
    </div>
  );
};

const AuthLayout = ({ children }) => {
  const { profile, token, signOut, isEmployee } = useContext(AuthContext);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);

  const [currentPage, setCurrentPage] = useAtom(currentPageStore)

  // Condiviso con la home del dipendente (banner) e aggiornato dalla pagina /areariservata/firme
  const [firmePending, setFirmePending] = useAtom(firmePendingStore);
  const canSeeFirme = isEmployee && canViewOwnSignedDocs(profile);
  // Il layout resta montato tra una pagina e l'altra: il conteggio si ricarica entrando/uscendo dalla home.
  // Si usa l'URL e non currentPage, che cambia solo con i click sul menu
  const [location] = useLocation();
  const onHome = location === '/' || location === '/areariservata';

  useEffect(() => {
    if (!token || !canSeeFirme) {
      setFirmePending(0);
      return;
    }

    let active = true;

    employeeSignedDocumentsStats(token)
      .then((stats) => {
        if (!active) return;
        setFirmePending(stats?.da_firmare ?? 0);
      })
      .catch((error) => {
        if (!active) return;
        console.error('Errore caricamento statistiche firme:', error);
        setFirmePending(0);
      });

    return () => {
      active = false;
    };
  }, [token, canSeeFirme, onHome, setFirmePending]);

  // Al logout il layout viene smontato: il conteggio non deve restare per l'utente successivo
  useEffect(() => () => setFirmePending(0), []);

  const canViewLivelli = profile?.can_purchase

  const hasCustomLetters = Object.values(profile?.level ?? {}).some(
      features => Array.isArray(features) && features.includes('custom_letters')
  );

  const canViewLetters =
    profile?.customer_type === 'employee'
      ? hasCustomLetters
      : profile?.can_purchase === true
        ? true
        : hasCustomLetters;

  useEffect(() => {
    const reactTarget = document.getElementById('react-target');

    // Add the class when the component mounts
    if (reactTarget) {
      reactTarget.classList.add('app');
    }

    // Remove the class when the component unmounts
    return () => {
      if (reactTarget) {
        reactTarget.classList.remove('app');
      }
    };
  }, []);

  return (
    <>
      <div className="min-h-full">

        {/* VISUALIZZAZIONE MOBILE */}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative lg:hidden" style={{ zIndex: '300' }} onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white pb-4 pt-5">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute right-0 top-0 -mr-12 pt-2">
                      <button
                        type="button"
                        className="relative ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="absolute -inset-0.5" />
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex flex-shrink-0 items-center px-4">
                    {/* <img
                      className="h-8 w-auto"
                      src={profile.logo_encoded}
                      alt={profile.employee?.first_name + ' ' + profile.employee?.last_name}
                    /> */}
                    <img
                      className="h-8 w-auto"
                      src="https://consultala.com/static/images/consultala2.png"
                      alt={profile?.company?.name}
                    />
                  </div>
                  <div className="mt-5 h-0 flex-1 overflow-y-auto">
                    <nav className="px-2">
                      <div className="space-y-1">
                        <div className="my-4 px-4">
                          <Menu as="div" className="relative inline-block w-full text-left">
                            <div>
                              <Menu.Button className="flex w-full items-center justify-between rounded-md bg-indigo-50 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                <span className="flex min-w-0 items-center space-x-3">
                                  {/* employee */}
                                  {isEmployee && (
                                    <>
                                      <UserCircleIcon className="h-8 w-8 flex-shrink-0 text-gray-500" />
                                      <span className="truncate">
                                        {profile.employee?.first_name} {profile.employee?.last_name}
                                      </span>
                                    </>
                                  )}

                                  {/* consultant */}
                                  {profile.customer_type === 'consultant' && (
                                    <>
                                      {profile.logo_encoded ? (
                                        <img
                                          className="h-8 w-8 flex-shrink-0 rounded-full bg-gray-300"
                                          src={profile.logo_encoded}
                                          alt="Your Company Logo"
                                        />
                                      ) : (
                                        <UserCircleIcon className="h-8 w-8 flex-shrink-0 text-gray-500" />
                                      )}
                                      <span className="truncate">
                                        {profile.company?.name}
                                      </span>
                                    </>
                                  )}

                                  {/* company */}
                                  {profile.customer_type === 'company' && (
                                    <>
                                      {profile.logo_encoded ? (
                                        <img
                                          className="h-8 w-8 flex-shrink-0 rounded-full bg-gray-300"
                                          src={profile.logo_encoded}
                                          alt="Your Company Logo"
                                        />
                                      ) : (
                                        <UserCircleIcon className="h-8 w-8 flex-shrink-0 text-gray-500" />
                                      )}
                                      <span className="truncate">
                                        {profile.company?.name}
                                      </span>
                                    </>
                                  )}
                                </span>

                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Menu.Button>
                            </div>

                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <Menu.Items className="absolute z-10 mt-2 w-full origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div className="py-1">
                                  <MenuItemsProfilo profile={profile} isEmployee={isEmployee} canViewLivelli={canViewLivelli} />

                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        type="button"
                                        onClick={signOut}
                                        className={classNames(
                                          active ? 'bg-gray-100' : '',
                                          'block w-full px-4 py-2 text-left text-sm text-gray-700'
                                        )}
                                      >
                                        Esci
                                      </button>
                                    )}
                                  </Menu.Item>
                                </div>
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        </div>

                        {/* MENù EMPLOYEE VISUALIZZAZIONE MOBILE */}
                        {isEmployee && (
                          <EmployeeLink 
                            currentPage={currentPage} 
                            profile={profile}
                            setCurrentPage={setCurrentPage}
                            desktopSidebarOpen={true}
                            firmePending={firmePending}
                            canViewLetters={canViewLetters}
                          />
                        )}

                        {/* MENù CONSULTANT VISUALIZZAZIONE MOBILE */}
                        {profile.customer_type === 'consultant' && (
                          <ConsultantLink
                            currentPage={currentPage} 
                            profile={profile}
                            setCurrentPage={setCurrentPage}
                            desktopSidebarOpen={true}
                            canViewLetters={canViewLetters}
                          />
                        )}

                        {/* MENù COMPANY VISUALIZZAZIONE MOBILE */}
                        {profile.customer_type === 'company' && (
                          <ConsultantLink
                            currentPage={currentPage} 
                            profile={profile}
                            setCurrentPage={setCurrentPage}
                            desktopSidebarOpen={true}
                            canViewLetters={canViewLetters}
                          />
                        )}

                      </div>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className="w-14 flex-shrink-0" aria-hidden="true">
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* VISUALIZZAZIONE DESKTOP - Static sidebar for desktop */}
        <div
          className={classNames(
            "hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col lg:border-r lg:border-indigo-300 lg:bg-indigo-50 lg:pb-4 lg:pt-5 transition-all duration-200",
            desktopSidebarOpen ? "lg:w-64" : "lg:w-[4.6rem]"
          )}
          style={{ zIndex: '200' }}
        >
          <div className="relative flex flex-shrink-0 items-center justify-center px-2 lg:px-4">
            {/* Logo piccolo quando la barra è chiusa */}
            <img
              className={classNames(
                "h-8 w-8 block transition-all duration-200 brightness-75",
                desktopSidebarOpen && "lg:hidden"
              )}
              src="https://consultala.com/static/images/favicon/android-chrome-512x512.png"
              alt={profile?.company?.name}
            />

            {/* Logo completo quando la barra è aperta */}
            <img
              className={classNames(
                "h-8 w-auto hidden transition-all duration-200",
                desktopSidebarOpen && "lg:block"
              )}
              src="https://consultala.com/static/images/consultala2.png"
              alt={profile?.company?.name}
            />

            {/* BOTTONE TOGGLE */}
            <CustomTooltipComponent 
              title={desktopSidebarOpen ? "Chiudi" : "Apri"} 
              placement="right"
            >
              <button
                type="button"
                onClick={() => setDesktopSidebarOpen((prev) => !prev)}
                className="
                  hidden lg:flex
                  items-center justify-center
                  absolute -right-3 top-1/2 -translate-y-1/2
                  h-6 w-6 rounded-full border border-indigo-300 bg-white shadow-sm
                  text-indigo-600 hover:bg-indigo-50
                  cursor-pointer
                "
              >
                <div className="flex gap-1">
                  <span className={`h-2 w-2 rounded-full ${desktopSidebarOpen ? 'bg-indigo-400' : 'bg-indigo-200'}`} />
                  <span className={`h-2 w-2 rounded-full ${desktopSidebarOpen ? 'bg-indigo-200' : 'bg-indigo-400'}`} />
                </div>
              </button>
            </CustomTooltipComponent>
          </div>
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="mt-5 flex h-0 flex-1 flex-col overflow-y-auto pt-1"
            style={{
              scrollbarWidth: "thin",            // Firefox
              scrollbarColor: "#c7d2fe transparent"
            }}
          > 
            {desktopSidebarOpen && (
              <Menu as="div" className="relative inline-block px-3 text-left">
                <div>
                  <Menu.Button className="group w-full rounded-md bg-gray-100 px-3.5 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                    <span className="flex w-full items-center justify-between">
                      <span className="flex min-w-0 items-center justify-between space-x-3">

                        {/* NOME PROFILO EMPLOYEE VISUALIZZAZIONE DESKTOP */}
                        {isEmployee && (
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="break-words text-sm font-medium text-gray-900">
                              {profile.employee?.first_name + ' ' + profile.employee?.last_name}
                            </span>
                          </span>
                        )}

                        {/* LOGO E NOME PROFILO CONSULTANT - VISUALIZZAZIONE DESKTOP */}
                        {profile.customer_type === 'consultant' && (
                          <span className="flex items-center space-x-3">
                            {profile.logo_encoded ? (
                              <img
                                className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-300"
                                src={profile.logo_encoded}
                                alt="Your Company Logo"
                              />
                            ) : (
                              <UserCircleIcon className="h-10 w-10 flex-shrink-0 text-gray-500" />
                            )}
                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="break-words text-sm font-medium text-gray-900">
                                {profile.company?.name}
                              </span>
                            </span>
                          </span>
                        )}

                        {/* LOGO E NOME PROFILO COMPANY - VISUALIZZAZIONE DESKTOP */}
                        {profile.customer_type === 'company' && (
                          <span className="flex items-center space-x-3">
                            {profile.logo_encoded ? (
                              <img
                                className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-300"
                                src={profile?.logo_encoded}
                                alt="Your Company Logo"
                              />
                            ) : (
                              <UserCircleIcon className="h-10 w-10 flex-shrink-0 text-gray-500" />
                            )}
                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="break-words text-sm font-medium text-gray-900">{profile?.company?.name}</span>
                            </span>
                          </span>
                        )}
                      </span>
                      <ChevronUpDownIcon
                        className="h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                    </span>
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items >
                    <div className="py-1">
                    
                      <MenuItemsProfilo profile={profile} isEmployee={isEmployee} canViewLivelli={canViewLivelli} />

                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            onClick={signOut}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'block px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            Esci
                          </a>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
            <nav className="mt-6 px-3">
              <div className="space-y-1">

                {isEmployee && (
                  <EmployeeLink 
                    currentPage={currentPage} 
                    profile={profile}
                    setCurrentPage={setCurrentPage}
                    desktopSidebarOpen={desktopSidebarOpen}
                    firmePending={firmePending}
                    canViewLetters={canViewLetters}
                  />  
                )}

                {profile.customer_type === 'consultant' && (
                  <ConsultantLink
                    currentPage={currentPage} 
                    profile={profile}
                    setCurrentPage={setCurrentPage}
                    desktopSidebarOpen={desktopSidebarOpen}
                    canViewLetters={canViewLetters}
                  />
                )}

                {profile.customer_type === 'company' && (
                  <ConsultantLink
                    currentPage={currentPage} 
                    profile={profile}
                    setCurrentPage={setCurrentPage}
                    desktopSidebarOpen={desktopSidebarOpen}
                    canViewLetters={canViewLetters}
                  />
                )}
              </div>
            </nav>
          </div>
        </div>

        {/* Main column */}
        <div
          className={classNames(
            "flex flex-col transition-all duration-200",
            desktopSidebarOpen ? "lg:pl-64" : "lg:pl-16"
          )}
          style={{ zIndex: "200" }}
        >
          {/* Search header */}
          <div className="sticky top-0 flex h-16 flex-shrink-0 border-b border-gray-200 bg-white lg:hidden" style={{ zIndex: '200' }}>
            <button
              type="button"
              className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3CenterLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex flex-1 justify-end px-4 sm:px-6 lg:px-8">
              <div className="flex items-center">
                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button className="relative flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2">
                      <span className="absolute -inset-1.5" />
                      <span className="sr-only">Open user menu</span>
                      <span className="flex items-center space-x-3">
                        {profile.customer_type === 'consultant' && (
                          <span className="flex items-center space-x-3">
                            {profile.logo_encoded ? (
                              <img
                                className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-300"
                                src={profile.logo_encoded}
                                alt="Your Company Logo"
                              />
                            ) : (
                              <UserCircleIcon className="h-10 w-10 flex-shrink-0 text-gray-500" />
                            )}
                          </span>
                        )}
                        {profile.customer_type === 'company' && (
                          <span className="flex items-center space-x-3">
                            {profile.logo_encoded ? (
                              <img
                                className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-300"
                                src={profile.logo_encoded}
                                alt="Your Company Logo"
                              />
                            ) : (
                              <UserCircleIcon className="h-10 w-10 flex-shrink-0 text-gray-500" />
                            )}
                          </span>
                        )}
                        {isEmployee && (
                          <span className="flex items-center space-x-3">
                              <UserCircleIcon className="h-10 w-10 flex-shrink-0 text-gray-500" />
                          </span>
                        )}
                      </span>
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    {/* MENU ITEMS DEL PROFILO */}
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right divide-y divide-gray-200 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">

                        <MenuItemsProfilo profile={profile} isEmployee={isEmployee} />
                          
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href="#"
                              onClick={signOut}
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              Esci
                            </a>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
          <main className="flex-1">
            {children}
          </main>
        </div>
      </div>
    </>
  )
};

export default AuthLayout;


const EmployeeLink = ({ currentPage, profile, setCurrentPage, desktopSidebarOpen, firmePending, canViewLetters }) => {
  return (
    <>
      {/* LINK DASHBOARD PERSONALE DELL'EMPLOYEE */}
      <div className="mb-4">
        {renderMenuGroup({
          gestioneKey: 'navigazione',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
        })}
      </div>


    {/* MENU PERSONALE DEL DIPENDENTE */}
    <div>
      <SidebarSectionHeader
        title="Area Personale"
        icon={UserCircleIcon}
        desktopSidebarOpen={desktopSidebarOpen}
      />
        {renderMenuGroup({
          gestioneKey: 'areariservata',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
          badges: { firmePending },
        })}
    </div>


    {/* EMPLOYEE è MANAGER - AGGIUNTA ITEMS GESTIONE BASE (unità org, lista dip, aggiungi dip, buste paga, CU, docs) */}
    {/* GESTIONE BASE */}
    {profile.is_manager && (
      <div>
        <SidebarSectionHeader
          title="Area Manager"
          icon={UserGroupIcon}
          desktopSidebarOpen={desktopSidebarOpen}
          showDivider
        />

        <h3
          className={classNames(
            "mb-1 mt-4 px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500",
            !desktopSidebarOpen && "lg:hidden"
          )}
        >
          Base
        </h3>

        {renderMenuGroup({
          gestioneKey: 'gestione-base',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
        })}
      </div>
    )}

      {/* EMPLOYEE è MANAGER - AGGIUNTA ITEMS GESTIONE PRESENZE (gestione turni, assegna turni, calendario presenze, richieste giustificativi, avvisi) */}
      { // PRESENZE
        profile.is_manager && (
          <div>
            <h3
              className={classNames(
                "mb-1 mt-4 px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500",
                !desktopSidebarOpen && "lg:hidden"
              )}
            >
              Presenze
            </h3>
            {renderMenuGroup({
              gestioneKey: 'gestione-presenze',
              currentPage,
              setCurrentPage,
              desktopSidebarOpen,
            })}
          </div>
        )
      }

      {/* EMPLOYEE è MANAGER - AGGIUNTA ITEMS GESTIONE PRESENZE (gestione turni, assegna turni, calendario presenze, richieste giustificativi, avvisi) */}
      { // STRUMENTI - CALCOLO COSTO DEL LAVORO
        profile.is_manager && (
          <div className="">
            <h3
              className={classNames(
                "mt-4 px-3 text-sm font-semibold text-gray-500",
                !desktopSidebarOpen && "lg:hidden"
              )}
            >
              Strumenti
            </h3>
            {renderMenuGroup({
              gestioneKey: 'strumenti',
              currentPage,
              setCurrentPage,
              desktopSidebarOpen,
              canViewLetters,
            })}
          </div>
        )
      }
    </>
  )
}


const ConsultantLink = ({ currentPage, profile, setCurrentPage, desktopSidebarOpen, canViewLetters }) => {
  return (
    <>
      {/* LINK DASHBOARD CONSULTANT/COMPANY */}
      <div className="mb-4">
        {renderMenuGroup({
          gestioneKey: 'navigazione',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
        })}
      </div>

          
      {/* DOCUMENTI AZIENDA */}
      <div className="">
        <h3
          className={classNames(
            "mt-4 px-3 text-sm font-semibold text-gray-500",
            !desktopSidebarOpen && "lg:hidden"
          )}
        >
          Documenti Azienda
        </h3>
        {renderMenuGroup({
          gestioneKey: 'documenti-azienda',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
        })}
      </div>

      {/* MENù PRESENZE */}
      <div className="">
        <h3
          className={classNames(
            "mt-4 px-3 text-sm font-semibold text-gray-500",
            !desktopSidebarOpen && "lg:hidden"
          )}
        >
          Presenze
        </h3>
        {renderMenuGroup({
          gestioneKey: 'presenze',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
        })}
      </div>

      {/* MENù GESTIONE DIPENDENTI  */}
      <div className="">
        <h3
          className={classNames(
            "mt-4 px-3 text-sm font-semibold text-gray-500",
            !desktopSidebarOpen && "lg:hidden"
          )}
        >
          Dipendenti
        </h3>
        {renderMenuGroup({
          gestioneKey: 'dipendenti',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
        })}
      </div>

      {/* MENù GESTIONE DOCUMENTI */}
      <div className="">
        <h3
          className={classNames(
            "mt-4 px-3 text-sm font-semibold text-gray-500",
            !desktopSidebarOpen && "lg:hidden"
          )}
        >
          Gestione Documenti
        </h3>
        {renderMenuGroup({
          gestioneKey: 'documenti',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
        })}
      </div>

      {/* MENù STRUMENTI  */}
      <div className="">
        <h3
          className={classNames(
            "mt-4 px-3 text-sm font-semibold text-gray-500",
            !desktopSidebarOpen && "lg:hidden"
          )}
        >
          Strumenti
        </h3>
        {renderMenuGroup({
          gestioneKey: 'strumenti',
          currentPage,
          setCurrentPage,
          desktopSidebarOpen,
          canViewLetters,
        })}
      </div>
    </>
  )
}

const MenuItemsProfilo = ({ profile, isEmployee, canViewLivelli }) => { 
  return (
    <>
      <Menu.Item>
        {({ active }) => (
          <>
            {isEmployee ? (
              <Link to="/areariservata/profilo" className="block px-4 py-2 text-sm text-gray-700">
                Profilo
              </Link>
            ) : profile.customer_type === 'consultant' ? (
              <Link to="/profilo" className="block px-4 py-2 text-sm text-gray-700">
                Profilo
              </Link>
            ) : profile.customer_type === 'company' ? (
              <Link to="/profilo" className="block px-4 py-2 text-sm text-gray-700">
                Profilo
              </Link>
            ) : null}
          </>
        )}
      </Menu.Item>

      {profile?.customer_type === 'company' && (profile?.company?.email == profile?.the_real_user_email) && (
        <Menu.Item>
          {({ active }) => (
            <Link to="/profilo/fatturazione" className='block px-4 py-2 text-sm text-gray-700'>
              Profilo Fatturazione
            </Link>
          )}
        </Menu.Item>
      )}
  
      <Menu.Item>
        {({ active }) => (
          <Link 
            to="/livelli" 
            className={`px-4 py-2 text-sm text-gray-700 ${(isEmployee) ? 'hidden' : 'block'}`}
          >
            Abbonamento
          </Link>
        )}
      </Menu.Item>

      {!isEmployee && ( 
        <Menu.Item>
          {({ active }) => (
            <a
              href="https://consultalahr.com/videoguida/"
              target="_blank"
              rel="noopener noreferrer"
              className='block px-4 py-2 text-sm text-gray-700'
            >
              Guida Utente
            </a>
          )}
        </Menu.Item>
      )}

    </>
  );
};
